<template>
  <div class="sp-card" :class="classModifiers" part="root" v-bind="rootAttrs">
    <div class="sp-card__content-overlay">
      <slot name="content-overlay" />
    </div>
    <div class="sp-card__title">
      <slot name="title">
        <sp-card-title v-if="title">{{ title }}</sp-card-title>
      </slot>
    </div>
    <div class="sp-card__subtitle">
      <slot name="subtitle">
        <sp-card-subtitle v-if="subtitle">{{ subtitle }}</sp-card-subtitle>
      </slot>
    </div>
    <div class="sp-card__content">
      <slot />
    </div>
    <div class="sp-card__actions">
      <slot name="actions" />
    </div>
  </div>
</template>

<script setup>
import { computed, useAttrs } from "vue";
import { useVariant } from "#ce/composables/useVariant";
import { omitAttrs } from "#ce/utils/forwarded-attrs";
import { toBoolean } from "#ce/utils/props";
import { props as cardProps } from "./props.js";

defineOptions({ inheritAttrs: false });

const props = defineProps(cardProps);
const attrs = useAttrs();
const title = computed(() => attrs.title);
const rootAttrs = computed(() => omitAttrs(attrs, ["title"]));
const { variantModifiers } = useVariant(props);
const classModifiers = computed(() => [
  {
    "--grid": toBoolean(props.grid) || toBoolean(props.scrollableContent),
    "--scrollable-content": toBoolean(props.scrollableContent),
    [`--elevation-${props.elevation}`]: props.elevation,
  },
  ...variantModifiers.value,
]);
</script>

<style>
:host {
  --card-border: var(--sp-comp-card-border, none);
  display: block;
  overflow: visible;
  height: var(--sp-card-root-height, auto);
}
</style>

<style scoped lang="scss">
.sp-card {
  --card-title-z-index: var(--sp-comp-card-title-z-index, 99);

  position: relative;
  border: var(--card-border);
  border-radius: var(--sp-comp-card-border-radius, var(--sp-sys-shape-control));
  background: var(--sp-comp-card-background, transparent);
  box-sizing: border-box;
  overflow: var(--sp-comp-card-overflow, hidden);

  &.--grid {
    display: grid;
    grid-template-rows: auto auto 1fr auto;
    grid-template-areas: "title" "subtitle" "content" "actions";
    height: var(--sp-comp-card-height, auto);
    max-height: var(--sp-comp-card-max-height, none);

    .sp-card__content {
      position: relative;
      overflow-y: var(--sp-comp-card-content-overflow-y, hidden);
      // Without min-height: 0, a tall child can force this grid row taller than
      // the card. Let it shrink so the chosen overflow rule can take effect.
      min-height: 0;
    }
  }

  &.--scrollable-content {
    .sp-card__content {
      overflow-y: auto !important;
    }
  }

  &.--variant-outlined {
    border: var(--sp-comp-card-outlined-border, 1px solid var(--sp-sys-color-outline));
  }

  &.--elevation-1,
  &.--variant-elevated {
    box-shadow: var(--sp-sys-elevation-1);
  }

  &.--elevation-2 {
    box-shadow: var(--sp-sys-elevation-2);
  }

  &.--elevation-3 {
    box-shadow: var(--sp-sys-elevation-3);
  }
  // Sets card padding to 0
  &.--variant-plain {
    --sp-comp-card-padding-inline: 0;
    --sp-comp-card-padding-block: 0;
  }
  // Comfortable padding for a card inside a surface that supplies its own
  // border/shadow (e.g. an sp-popup menu). Smaller top edge keeps the first row
  // close to the activator. Tokens stay overridable per consumer.
  &.--variant-new-default {
    --sp-comp-card-padding-inline: var(--sp-ref-scale-16); // 16px
    // top bottom shorthand: 12px top, 16px bottom.
    --sp-comp-card-padding-block: var(--sp-ref-scale-12) var(--sp-ref-scale-16);
  }
  // Removes card shadow and border
  &.--variant-plain,
  &.--variant-flat,
  &.--variant-new-default {
    box-shadow: none !important;
    border: none !important;
  }
}

.sp-card__title {
  grid-area: title;
  z-index: var(--card-title-z-index);
}

.sp-card__subtitle {
  grid-area: subtitle;
}

.sp-card__content {
  grid-area: content;
  position: relative;
  height: var(--sp-comp-card-content-height, 100%);
}

.sp-card__actions {
  grid-area: actions;
}

.sp-card__content-overlay {
  z-index: calc(var(--card-title-z-index) - 1);
}
</style>
